/**
 * Hook to register handlers for all potential fields based on schema,
 * not just rendered ones. This ensures pagination doesn't break dependency handling.
 */

import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { JSONSchema7 } from 'json-schema';
import { UseDependencyManagerResult } from './useDependencyManager';
import {
  findEntitySelectionPickerFields,
  expandTemplateFieldPath,
} from './schemaHandlerRegistry';
import { DEPENDENCY_MANAGER_ID_SEPARATOR } from '../utils';
import {
  buildResolvedObject,
  resolveEntities,
} from '../../fields/EntitySelectionPicker/EntitySelectionPicker.utils';
import { getValueAtPath } from './batchedUpdate';

export interface UseSchemaHandlerRegistrationOptions {
  /** The JSON Schema for the form */
  schema: JSONSchema7;
  /** UI Schema with field metadata */
  uiSchema?: any;
  /** Current form data */
  formData: any;
  /** The dependency manager instance */
  dependencyManager?: UseDependencyManagerResult | null;
  /** Field path separator */
  separator?: string;
}

/**
 * Hook that registers handlers for all potential EntitySelectionPicker fields
 * based on the schema, regardless of whether they're currently rendered.
 *
 * This solves the pagination problem where handlers only register for visible items.
 */
export function useSchemaHandlerRegistration(
  options: UseSchemaHandlerRegistrationOptions,
): void {
  const {
    schema,
    uiSchema,
    formData,
    dependencyManager,
    separator = DEPENDENCY_MANAGER_ID_SEPARATOR,
  } = options;
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const registrationsRef = useRef<{
    middlewareId?: string;
    fieldPaths: Set<string>;
  }>({ fieldPaths: new Set() });

  useEffect(() => {
    if (!dependencyManager?.isReady || !dependencyManager.id) {
      return undefined;
    }

    if (registrationsRef.current.middlewareId !== dependencyManager.id) {
      registrationsRef.current = {
        middlewareId: dependencyManager.id,
        fieldPaths: new Set(),
      };
    }

    // Find all EntitySelectionPicker fields in the schema
    const pickerFields = findEntitySelectionPickerFields(
      schema,
      uiSchema,
      '',
      separator,
    );

    const entityResolutionPromises = new Map<
      string,
      Promise<Record<string, any>[]>
    >();
    const dependenciesToInitialize = new Set<string>();

    const resolveReferencedEntities = (
      referencedValue: Record<string, any> | string | any[],
    ) => {
      const references = Array.isArray(referencedValue)
        ? referencedValue
        : [referencedValue];
      const cacheKey = JSON.stringify(references);
      let resolutionPromise = entityResolutionPromises.get(cacheKey);

      if (!resolutionPromise) {
        resolutionPromise = identityApi
          .getCredentials()
          .then(credentials =>
            resolveEntities(references, {
              catalogApi,
              token: credentials.token,
            }),
          )
          .finally(() => entityResolutionPromises.delete(cacheKey));
        entityResolutionPromises.set(cacheKey, resolutionPromise);
      }

      return resolutionPromise;
    };

    pickerFields.forEach(pickerField => {
      const templatePath = pickerField.fieldPath;

      dependencyManager.registerDependencies(templatePath, [
        pickerField.dependsOn,
      ]);

      // Expand template path to all actual indices
      const expandedPaths = expandTemplateFieldPath(
        templatePath,
        formData,
        separator,
      );

      // Register a default handler for each expanded path
      expandedPaths.forEach(expandedPath => {
        if (registrationsRef.current.fieldPaths.has(expandedPath)) return;
        registrationsRef.current.fieldPaths.add(expandedPath);
        dependenciesToInitialize.add(pickerField.dependsOn);

        dependencyManager.registerHandler(
          expandedPath,
          (
            fieldPath: string,
            triggerPath: string,
            triggerValue: any,
            currentFormData: any,
          ) => {
            const currentValue = getValueAtPath(
              currentFormData,
              fieldPath,
              separator,
            );

            const dependencyPath = pickerField.dependsOn;
            const isDirectTrigger = triggerPath === dependencyPath;
            const isSubFieldTrigger = triggerPath.startsWith(
              `${dependencyPath}${separator}`,
            );
            let referencedValue = isDirectTrigger
              ? triggerValue
              : getValueAtPath(currentFormData, dependencyPath, separator);

            if (
              isSubFieldTrigger &&
              referencedValue &&
              typeof referencedValue === 'object'
            ) {
              const subFieldPath = triggerPath
                .slice(dependencyPath.length + separator.length)
                .split(separator);
              referencedValue = _.set(
                _.cloneDeep(referencedValue),
                subFieldPath,
                triggerValue,
              );
            }

            if (!referencedValue) {
              return isDirectTrigger
                ? pickerField.schema.default
                : currentValue;
            }

            const getPropertyValue = (entity: Record<string, any>) => {
              const propertyPath = pickerField.pickerConfig['ui:property'];
              if (propertyPath) {
                return (
                  _.get(entity, propertyPath) ?? pickerField.schema.default
                );
              }

              const mapTo = pickerField.pickerConfig.mapTo;
              return mapTo ? buildResolvedObject(entity, mapTo) : entity;
            };

            const resolvePickerValue = (propertyValues: any[]) => {
              if (
                propertyValues.length === 1 &&
                pickerField.schema.type !== 'array'
              ) {
                return propertyValues[0];
              }

              return propertyValues;
            };

            if (
              typeof referencedValue === 'object' &&
              !Array.isArray(referencedValue)
            ) {
              return resolvePickerValue(
                [referencedValue].flatMap(getPropertyValue),
              );
            }

            return resolveReferencedEntities(referencedValue)
              .then(entities =>
                entities.length
                  ? resolvePickerValue(entities.flatMap(getPropertyValue))
                  : pickerField.schema.default,
              )
              .catch(() => currentValue ?? pickerField.schema.default);
          },
        );
      });
    });

    dependenciesToInitialize.forEach(dependencyPath => {
      const dependencyValue = getValueAtPath(
        formData,
        dependencyPath,
        separator,
      );
      if (
        dependencyValue !== undefined &&
        dependencyValue !== null &&
        dependencyValue !== ''
      ) {
        dependencyManager.notifyUpdate({
          fieldPath: dependencyPath,
          value: dependencyValue,
          source: 'system',
          intent: 'manual',
        });
      }
    });

    return undefined;
  }, [
    schema,
    uiSchema,
    formData,
    dependencyManager,
    separator,
    catalogApi,
    identityApi,
  ]);
}
